import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign, ShoppingCart, Users, Package,
  TrendingUp, ArrowUpRight, Loader2, AlertCircle,
  Clock, CheckCircle, XCircle,
} from 'lucide-react';
import { supabase } from '@/utils/supabase';

function StatCard({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-[var(--paper)] border border-[var(--line)] p-5">
      <div className="flex items-start justify-between mb-3">
        <p className="font-['Space_Mono'] text-[0.6rem] tracking-[0.16em] uppercase text-[var(--muted)]">
          {label}
        </p>
        <div className={`w-7 h-7 flex items-center justify-center ${accent || 'bg-white/5'}`}>
          <Icon size={13} className={accent ? 'text-[#09090b]' : 'text-[var(--muted)]'} />
        </div>
      </div>
      <p className="text-2xl font-['Cormorant_Garamond'] text-[var(--ink)]">{value}</p>
      {sub && (
        <p className="text-[0.65rem] font-['Space_Mono'] text-[var(--muted)] mt-1">{sub}</p>
      )}
    </div>
  );
}

function fmtCurrency(cents) {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const map = {
    paid:        { label: 'Paid',        cls: 'text-emerald-400 bg-emerald-400/10', icon: CheckCircle },
    unfulfilled: { label: 'Unfulfilled', cls: 'text-amber-400 bg-amber-400/10',     icon: Clock },
    refunded:    { label: 'Refunded',    cls: 'text-red-400 bg-red-400/10',         icon: XCircle },
  };
  const { label, cls, icon: Icon } = map[status] || { label: status, cls: 'text-[var(--muted)] bg-white/5', icon: Clock };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[0.6rem] font-['Space_Mono'] tracking-[0.08em] uppercase ${cls}`}>
      <Icon size={10} />
      {label}
    </span>
  );
}

export default function AdminDashboard() {
  const [stats, setStats]         = useState(null);
  const [recentOrders, setRecent] = useState([]);
  const [topProducts, setTopProds] = useState([]);
  const [lowStock, setLowStock]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const [
        { data: ordersAll },
        { data: orders30d },
        { data: recent },
        { data: products },
      ] = await Promise.all([
        supabase.from('orders').select('amount_total, customer_email').eq('status', 'paid'),
        supabase.from('orders').select('amount_total, customer_email').eq('status', 'paid')
          .gte('created_at', new Date(Date.now() - 30 * 86400000).toISOString()),
        supabase.from('orders').select('*').order('created_at', { ascending: false }).limit(8),
        supabase.from('products').select('*').eq('status', 'active').order('inventory', { ascending: true }),
      ]);

      const totalRev  = (ordersAll || []).reduce((s, o) => s + (o.amount_total || 0), 0);
      const rev30d    = (orders30d || []).reduce((s, o) => s + (o.amount_total || 0), 0);
      const customers = new Set((ordersAll || []).map((o) => o.customer_email).filter(Boolean)).size;
      const aov       = (ordersAll || []).length > 0 ? totalRev / ordersAll.length : 0;

      setStats({
        totalRev, rev30d,
        totalOrders: (ordersAll || []).length,
        orders30d: (orders30d || []).length,
        customers,
        aov,
        activeProducts: (products || []).length,
      });

      setRecent(recent || []);
      setLowStock((products || []).filter((p) => (p.inventory ?? 0) <= 5));

      // Top products by inventory sold (rough proxy)
      const sorted = [...(products || [])].sort((a, b) => (b.price || 0) - (a.price || 0));
      setTopProds(sorted.slice(0, 5));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={20} className="animate-spin text-[var(--brand)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center gap-2 text-red-400 text-sm font-['Space_Mono']">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl text-[var(--ink)]">Dashboard</h1>
        <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Total Revenue"
          value={fmtCurrency(stats.totalRev)}
          sub={`${fmtCurrency(stats.rev30d)} last 30 days`}
          icon={DollarSign}
          accent="bg-[var(--brand)]"
        />
        <StatCard
          label="Total Orders"
          value={stats.totalOrders.toLocaleString()}
          sub={`${stats.orders30d} last 30 days`}
          icon={ShoppingCart}
        />
        <StatCard
          label="Customers"
          value={stats.customers.toLocaleString()}
          sub="Unique buyers"
          icon={Users}
        />
        <StatCard
          label="Avg Order Value"
          value={fmtCurrency(stats.aov)}
          sub={`${stats.activeProducts} active products`}
          icon={TrendingUp}
        />
      </div>

      {/* Two-column grid */}
      <div className="grid lg:grid-cols-3 gap-4">

        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-[var(--paper)] border border-[var(--line)]">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--line)]">
            <h2 className="font-['Space_Mono'] text-[0.65rem] tracking-[0.16em] uppercase text-[var(--ink)]">
              Recent Orders
            </h2>
            <Link
              to="/admin/orders"
              className="font-['Space_Mono'] text-[0.6rem] tracking-[0.1em] uppercase text-[var(--muted)] hover:text-[var(--brand)] transition-colors flex items-center gap-1"
            >
              View all <ArrowUpRight size={10} />
            </Link>
          </div>
          <div className="divide-y divide-[var(--line)]">
            {recentOrders.length === 0 ? (
              <p className="text-[var(--muted)] text-xs font-['Space_Mono'] p-5">No orders yet.</p>
            ) : recentOrders.map((order) => (
              <Link
                key={order.id}
                to={`/admin/orders/${order.id}`}
                className="flex items-center justify-between px-5 py-3 hover:bg-white/3 transition-colors group"
              >
                <div className="min-w-0">
                  <p className="text-sm text-[var(--ink)] truncate">
                    {order.customer_name || order.customer_email || 'Unknown'}
                  </p>
                  <p className="font-['Space_Mono'] text-[0.58rem] tracking-[0.08em] text-[var(--muted)] mt-0.5">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <StatusBadge status={order.status} />
                  <span className="text-sm font-['Space_Mono'] text-[var(--ink)]">
                    {fmtCurrency(order.amount_total || 0)}
                  </span>
                  <ArrowUpRight size={11} className="text-[var(--muted)] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-4">
          {/* Low stock */}
          <div className="bg-[var(--paper)] border border-[var(--line)] flex-1">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--line)]">
              <h2 className="font-['Space_Mono'] text-[0.65rem] tracking-[0.16em] uppercase text-[var(--ink)]">
                Low Stock
              </h2>
              <Link
                to="/admin/products"
                className="font-['Space_Mono'] text-[0.6rem] tracking-[0.1em] uppercase text-[var(--muted)] hover:text-[var(--brand)] transition-colors"
              >
                <ArrowUpRight size={10} />
              </Link>
            </div>
            <div className="divide-y divide-[var(--line)]">
              {lowStock.length === 0 ? (
                <p className="text-[var(--muted)] text-xs font-['Space_Mono'] p-5">All stocked up.</p>
              ) : lowStock.slice(0, 6).map((p) => (
                <Link
                  key={p.id}
                  to={`/admin/products/${p.id}/edit`}
                  className="flex items-center justify-between px-5 py-2.5 hover:bg-white/3 transition-colors"
                >
                  <p className="text-xs text-[var(--ink)] truncate flex-1">{p.name}</p>
                  <span className={`ml-2 font-['Space_Mono'] text-[0.65rem] shrink-0 ${
                    (p.inventory ?? 0) === 0 ? 'text-red-400' : 'text-amber-400'
                  }`}>
                    {p.inventory ?? 0} left
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top products */}
      <div className="bg-[var(--paper)] border border-[var(--line)]">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--line)]">
          <h2 className="font-['Space_Mono'] text-[0.65rem] tracking-[0.16em] uppercase text-[var(--ink)]">
            Products
          </h2>
          <Link
            to="/admin/products"
            className="font-['Space_Mono'] text-[0.6rem] tracking-[0.1em] uppercase text-[var(--muted)] hover:text-[var(--brand)] transition-colors flex items-center gap-1"
          >
            Manage <ArrowUpRight size={10} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--line)]">
                {['Product', 'Price', 'Inventory', 'Status'].map((h) => (
                  <th key={h} className="text-left px-5 py-2.5 font-['Space_Mono'] text-[0.58rem] tracking-[0.12em] uppercase text-[var(--muted)]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {topProducts.map((p) => (
                <tr key={p.id} className="hover:bg-white/3 transition-colors">
                  <td className="px-5 py-3 text-[var(--ink)]">{p.name}</td>
                  <td className="px-5 py-3 font-['Space_Mono'] text-[var(--ink)]">
                    ${(p.price / 100).toFixed(2)}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`font-['Space_Mono'] ${
                      (p.inventory ?? 0) === 0 ? 'text-red-400' :
                      (p.inventory ?? 0) <= 5 ? 'text-amber-400' : 'text-emerald-400'
                    }`}>
                      {p.inventory ?? '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[0.58rem] font-['Space_Mono'] tracking-[0.08em] uppercase ${
                      p.status === 'active' ? 'text-emerald-400 bg-emerald-400/10' :
                      p.status === 'draft'  ? 'text-[var(--muted)] bg-white/5' :
                                              'text-red-400 bg-red-400/10'
                    }`}>
                      {p.status || 'active'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
